/* eslint-disable max-lines-per-function --
   TODO: 招募前 sprint 拆組件 (TODO-frontend-eslint-ci-warnings-as-errors)。
   2026-02-06 規則加入時 87 個既有違規凍結為 baseline，新檔/新函數仍受 hook 攔截。
*/
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ChangePasswordDialog from './ChangePasswordDialog';
import { getRoleLabel } from '../../constants/roles';

/**
 * 頂部導覽列組件
 *
 * 功能：
 * - 顯示網站標題
 * - 顯示主要功能選單（搜尋、上傳、管理）
 * - 顯示使用者帳號和登入/登出按鈕
 * - 根據使用者角色顯示不同選單項目
 *
 * Props:
 * @param {Function} onMenuClick - 點擊選單按鈕的回調函數（用於開啟側邊欄）
 * @param {Object} user - 使用者物件 { username, role }
 * @param {Function} onLogout - 登出回調函數
 */
const Header = ({ onMenuClick, user = null, onLogout }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // 處理導航
  const handleNavigate = (path) => {
    navigate(path);
  };

  // 開啟使用者選單
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // 關閉使用者選單
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // 處理登出
  const handleLogout = () => {
    handleMenuClose();
    if (onLogout) {
      onLogout();
    }
  };

  // 處理修改密碼
  const handleChangePassword = () => {
    handleMenuClose();
    setChangePasswordOpen(true);
  };

  // 處理個人資料
  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        {/* 選單按鈕（所有尺寸都顯示，用於收合側邊欄） */}
        <IconButton
          size="large"
          edge="start"
          color="inherit"
          aria-label="menu"
          sx={{ mr: 2 }}
          onClick={onMenuClick}
        >
          <MenuIcon />
        </IconButton>

        {/* 網站標題 */}
        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 0,
            fontWeight: 600,
            mr: 4,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
          }}
          onClick={() => handleNavigate('/')}
        >
          佛法搜尋資料庫
        </Typography>

        {/* 主要功能選單（大螢幕顯示） */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
          {/* 搜尋功能 - 所有使用者都可見 */}
          <Button
            color="inherit"
            startIcon={<SearchIcon />}
            sx={{ mx: 1 }}
            onClick={() => handleNavigate('/search')}
          >
            搜尋
          </Button>

          {/* 上傳功能 - 進階使用者和管理員可見 */}
          {user && (user.role === 'advanced' || user.role === 'admin') && (
            <Button
              color="inherit"
              startIcon={<UploadFileIcon />}
              sx={{ mx: 1 }}
              onClick={() => handleNavigate('/upload')}
            >
              上傳
            </Button>
          )}

          {/* 管理功能 - 僅管理員可見 */}
          {user && user.role === 'admin' && (
            <Button
              color="inherit"
              startIcon={<AdminPanelSettingsIcon />}
              sx={{ mx: 1 }}
              onClick={() => handleNavigate('/admin')}
            >
              管理
            </Button>
          )}
        </Box>

        {/* 使用者帳號區域 */}
        {user ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* 使用者名稱 */}
            <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
              {user.username}
            </Typography>

            {/* 使用者角色標籤 */}
            <Typography
              variant="caption"
              sx={{
                mr: 2,
                px: 1,
                py: 0.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 1,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {getRoleLabel(user.role)}
            </Typography>

            {/* 使用者選單按鈕 */}
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>

            {/* 使用者下拉選單 */}
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleProfile}>個人資料</MenuItem>
              <MenuItem onClick={handleChangePassword}>修改密碼</MenuItem>
              <MenuItem onClick={handleLogout}>登出</MenuItem>
            </Menu>
          </Box>
        ) : (
          /* 未登入狀態 - 顯示登入按鈕 */
          <Button color="inherit" variant="outlined" sx={{ ml: 2 }}>
            登入
          </Button>
        )}
      </Toolbar>

      {/* 修改密碼對話框 */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </AppBar>
  );
};

export default Header;
